import { Capacitor } from "@capacitor/core";
import { errorTracker } from "./errorTracker";
import {
  getSharedAccountHeaders,
  waitForSharedAccountAccess,
} from "./sharedAccount";
import { storage } from "./storage";
import { version } from "./constants";

const getErrorStatus = (error: unknown): number | undefined => {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const status = (error as { status?: unknown }).status;
  return typeof status === "number" ? status : undefined;
};

const isNotFoundError = (error: unknown): boolean => getErrorStatus(error) === 404;

const toHeadersObject = (headers?: HeadersInit): Record<string, string> => {
  if (!headers) {
    return {};
  }

  if (headers instanceof Headers) {
    return Object.fromEntries(headers.entries());
  }

  if (Array.isArray(headers)) {
    return Object.fromEntries(headers);
  }

  return { ...headers };
};

const buildHeaders = async (headers?: HeadersInit): Promise<Record<string, string>> => {
  const appInfo = {
    version,
    platform: Capacitor.isNativePlatform() ? Capacitor.getPlatform() : "web",
  };

  return {
    "Content-Type": "application/json",
    ...toHeadersObject(headers),
    ...(await getSharedAccountHeaders()),
    "X-App-Version": JSON.stringify(appInfo),
  };
};

async function post(url = "", data = {}, headers?: HeadersInit, method = "POST") {
  const finalHeaders = await buildHeaders(headers);

  try {
    const response = await fetch(url, {
      method,
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers: finalHeaders,
      redirect: "follow",
      referrerPolicy: "no-referrer",
      body: JSON.stringify(data),
    });

    if (response.status === 401) {
      const error = new Error(`Unauthorized ${location.href}`);
      (error as any)._tracked = true;
      errorTracker.captureError(error, "error", {
        url,
        method,
        status: response.status,
        errorType: "api_unauthorized",
      });
      throw error;
    }

    if (!response.ok) {
      let errorMessage = `HTTP Error: ${response.status} ${response.statusText}`;
      let responseData = null;

      try {
        const contentType = response.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          responseData = await response.json();
          errorMessage = responseData?.message || responseData?.error || errorMessage;
        }
      } catch (parseError) {
        // Keep default message when parsing fails.
      }

      const error = new Error(errorMessage);
      (error as any)._tracked = true;
      (error as any).response = responseData;
      (error as any).status = response.status;
      (error as any).statusText = response.statusText;
      errorTracker.captureError(error, "error", {
        url,
        method,
        status: response.status,
        statusText: response.statusText,
        errorType: "api_http_error",
        responseData,
      });
      throw error;
    }

    return response.json();
  } catch (error) {
    if (
      error instanceof Error &&
      !error.message.includes("Unauthorized") &&
      !error.message.includes("HTTP Error")
    ) {
      (error as any)._tracked = true;
      errorTracker.captureError(error, "error", {
        url,
        method,
        errorType: "api_network_error",
      });
    }
    throw error;
  }
}

async function get(url = "", headers?: HeadersInit, retries = 3, delay = 1000) {
  const finalHeaders = await buildHeaders(headers);

  for (let attempt = 0; attempt < retries; attempt++) {
    try {
      const response = await fetch(url, {
        method: "GET",
        mode: "cors",
        cache: "no-cache",
        credentials: "same-origin",
        headers: finalHeaders,
        redirect: "follow",
        referrerPolicy: "no-referrer",
      });

      if (response.status === 401) {
        const error = new Error(`Unauthorized ${location.href}`);
        (error as any)._tracked = true;
        errorTracker.captureError(error, "error", {
          url,
          method: "GET",
          status: response.status,
          attempt: attempt + 1,
          errorType: "api_unauthorized",
        });
        throw error;
      }

      if (!response.ok) {
        const error = new Error(`Server error: ${response.status}`);
        (error as any).status = response.status;

        if (attempt === retries - 1) {
          (error as any)._tracked = true;
          errorTracker.captureError(error, "error", {
            url,
            method: "GET",
            status: response.status,
            attempts: retries,
            errorType: "api_server_error",
          });
        }

        throw error;
      }

      return await response.json();
    } catch (error) {
      if (attempt < retries - 1) {
        console.warn(`GET request attempt ${attempt + 1} failed. Retrying in ${delay} ms...`);
        await new Promise((res) => setTimeout(res, delay));
      } else {
        if (
          error instanceof Error &&
          !error.message.includes("Unauthorized") &&
          !error.message.includes("Server error")
        ) {
          (error as any)._tracked = true;
          errorTracker.captureError(error, "error", {
            url,
            method: "GET",
            attempts: retries,
            errorType: "api_network_error",
          });
        }
        throw error;
      }
    }
  }
}

async function postWithJwt(url = "", data = {}) {
  await waitForSharedAccountAccess();
  const jwt = await storage.get("authJwt");

  if (jwt) {
    const bearerJwt = `Bearer ${jwt}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: bearerJwt,
    };
    return await post(url, data, headers);
  }
}

async function deleteWithJwt(url = "", data = {}) {
  await waitForSharedAccountAccess();
  const jwt = await storage.get("authJwt");

  if (jwt) {
    const bearerJwt = `Bearer ${jwt}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: bearerJwt,
    };
    return await post(url, data, headers, "DELETE");
  }
}

async function putWithJwt(url = "", data = {}) {
  await waitForSharedAccountAccess();
  const jwt = await storage.get("authJwt");

  if (jwt) {
    const bearerJwt = `Bearer ${jwt}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: bearerJwt,
    };
    return await post(url, data, headers, "PUT");
  }
}

async function patchWithJwt(url = "", data = {}) {
  await waitForSharedAccountAccess();
  const jwt = await storage.get("authJwt");

  if (jwt) {
    const bearerJwt = `Bearer ${jwt}`;
    const headers = {
      "Content-Type": "application/json",
      Authorization: bearerJwt,
    };
    return await post(url, data, headers, "PATCH");
  }
}

async function getWithJwt(url = "") {
  await waitForSharedAccountAccess();
  const jwt = await storage.get("authJwt");

  if (jwt) {
    const bearerJwt = `Bearer ${jwt}`;
    const headers = {
      "Content-Type": "application/json",
      authorization: bearerJwt,
    };
    return await get(url, headers, 3, 500);
  }
  throw new Error("No JWT token found");
}

async function getPdfWithJwt(url = "") {
  await waitForSharedAccountAccess();
  const jwt = await storage.get("authJwt");

  if (jwt) {
    const bearerJwt = `Bearer ${jwt}`;
    const headers = await buildHeaders({
      authorization: bearerJwt,
      "Content-Type": "application/pdf",
    });
    const response = await fetch(url, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      credentials: "same-origin",
      headers,
      redirect: "follow",
      referrerPolicy: "no-referrer",
    });

    return response.blob();
  }
}

export {
  get,
  post,
  postWithJwt,
  putWithJwt,
  getWithJwt,
  getPdfWithJwt,
  deleteWithJwt,
  patchWithJwt,
  getErrorStatus,
  isNotFoundError,
};
